import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { inventoryApi } from '../../api/inventoryApi';
import { formatQuantity } from '../../utils/formatQuantity';

interface Props {
  active: boolean;
  onNavigate?: (tab: string, itemId?: number) => void;
}

interface DashboardRow {
  inventoryItemId: number;
  name: string;
  unit: string;
  date: string; // yyyy-MM-dd
  used: number;
  stockIn: number;
  wasted: number;
}

interface InventoryItem {
  id: number;
  name: string;
  unit: string;
  currentStock: number;
  lowStockThreshold: number;
}

type Severity = 'CRITICAL' | 'WARN' | 'INFO';

interface Insight {
  id: string;
  severity: Severity;
  title: string;
  detail: string;
  actionLabel: string;
  /** tab passed to onNavigate */
  actionTab: string;
  itemId?: number;
}

interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

const SEV_RANK: Record<Severity, number> = { CRITICAL: 0, WARN: 1, INFO: 2 };

const SEV_COLOR = (theme: ReturnType<typeof useTheme>['theme'], sev: Severity): string => {
  if (sev === 'CRITICAL') return theme.danger || '#D32F2F';
  if (sev === 'WARN') return '#ED6C02';
  return '#0288D1';
};

const pad = (n: number) => String(n).padStart(2, '0');
const toISODate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const rangeForDays = (days: number): { from: string; to: string } => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - (days - 1));
  return { from: toISODate(from), to: toISODate(today) };
};

/** Sorted list of every date in [from, to] inclusive. */
const datesInRange = (from: string, to: string): string[] => {
  if (!from || !to || from > to) return [];
  const out: string[] = [];
  const end = new Date(`${to}T00:00:00`);
  const cursor = new Date(`${from}T00:00:00`);
  for (let i = 0; cursor <= end && i < 400; i += 1) {
    out.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const pct = (n: number) => Math.round(n * 100);

interface ItemStats {
  itemId: number;
  name: string;
  unit: string;
  totalUsed: number;
  totalWasted: number;
  usageDays: number; // distinct dates with used > 0
  wastageDays: number; // distinct dates with wasted > 0
  firstHalfUsed: number;
  secondHalfUsed: number;
}

/**
 * Derives inventory insights client-side from the dashboard movement rows plus the
 * current stock levels. No backend engine: every rule is computed honestly here and
 * skipped when the data is too thin, so a few good insights beat a wall of noise.
 */
const deriveInsights = (
  rows: DashboardRow[],
  items: InventoryItem[],
  from: string,
  to: string,
): Insight[] => {
  const dates = datesInRange(from, to);
  const rangeDays = dates.length || 1;
  const midIdx = Math.floor(dates.length / 2);
  const firstHalf = new Set(dates.slice(0, midIdx));

  const itemsById = new Map<number, InventoryItem>();
  items.forEach((it) => itemsById.set(it.id, it));

  // Aggregate movement stats per item.
  const stats = new Map<number, ItemStats>();
  const usageDates = new Map<number, Set<string>>();
  const wasteDates = new Map<number, Set<string>>();
  rows.forEach((r) => {
    let s = stats.get(r.inventoryItemId);
    if (!s) {
      s = {
        itemId: r.inventoryItemId,
        name: r.name,
        unit: r.unit,
        totalUsed: 0,
        totalWasted: 0,
        usageDays: 0,
        wastageDays: 0,
        firstHalfUsed: 0,
        secondHalfUsed: 0,
      };
      stats.set(r.inventoryItemId, s);
      usageDates.set(r.inventoryItemId, new Set());
      wasteDates.set(r.inventoryItemId, new Set());
    }
    s.totalUsed += r.used || 0;
    s.totalWasted += r.wasted || 0;
    if ((r.used || 0) > 0) {
      usageDates.get(r.inventoryItemId)!.add(r.date);
      if (firstHalf.has(r.date)) s.firstHalfUsed += r.used || 0;
      else s.secondHalfUsed += r.used || 0;
    }
    if ((r.wasted || 0) > 0) wasteDates.get(r.inventoryItemId)!.add(r.date);
  });
  stats.forEach((s) => {
    s.usageDays = usageDates.get(s.itemId)?.size || 0;
    s.wastageDays = wasteDates.get(s.itemId)?.size || 0;
  });

  const insights: Insight[] = [];

  // Rule 1 — Run-out risk (needs current stock + a usage rhythm).
  stats.forEach((s) => {
    if (s.usageDays < 3) return;
    const item = itemsById.get(s.itemId);
    if (!item || item.currentStock <= 0) return;
    const avgDaily = s.totalUsed / s.usageDays;
    if (avgDaily <= 0) return;
    const daysLeft = item.currentStock / avgDaily;
    if (daysLeft >= 5) return;
    const severity: Severity = daysLeft < 2 ? 'CRITICAL' : 'WARN';
    const daysShown = Math.max(0, Math.floor(daysLeft));
    insights.push({
      id: `runout-${s.itemId}`,
      severity,
      title: `${s.name} runs out in ~${daysShown} day${daysShown === 1 ? '' : 's'}`,
      detail: `~${formatQuantity(avgDaily, s.unit)} used/day, ${formatQuantity(item.currentStock, s.unit)} left.`,
      actionLabel: 'Stock in',
      actionTab: 'items',
      itemId: s.itemId,
    });
  });

  // Rule 2 — Wastage alert (recurring, or a big slice of usage).
  stats.forEach((s) => {
    if (s.totalWasted <= 0) return;
    const wastePct = s.totalUsed > 0 ? s.totalWasted / s.totalUsed : 1;
    const recurring = s.wastageDays >= 3;
    const heavy = s.totalUsed > 0 && wastePct >= 0.2;
    if (!recurring && !heavy) return;
    const reason = recurring
      ? `wasted on ${s.wastageDays} days`
      : `${pct(wastePct)}% of usage wasted`;
    insights.push({
      id: `wastage-${s.itemId}`,
      severity: 'WARN',
      title: `${s.name} wastage is adding up`,
      detail: `${formatQuantity(s.totalWasted, s.unit)} wasted in range — ${reason}.`,
      actionLabel: 'View variance',
      actionTab: 'variance',
      itemId: s.itemId,
    });
  });

  // Rule 3 — Usage trend (first vs second half of range).
  stats.forEach((s) => {
    if (s.usageDays < 4) return;
    if (s.firstHalfUsed <= 0) return;
    const change = (s.secondHalfUsed - s.firstHalfUsed) / s.firstHalfUsed;
    if (Math.abs(change) < 0.3) return;
    const up = change > 0;
    insights.push({
      id: `trend-${s.itemId}`,
      severity: 'INFO',
      title: `${s.name} usage ${up ? 'up' : 'down'} ${Math.abs(pct(change))}%`,
      detail: `Second half of the range vs first: ${formatQuantity(s.secondHalfUsed, s.unit)} vs ${formatQuantity(s.firstHalfUsed, s.unit)}.`,
      actionLabel: 'View usage',
      actionTab: 'overview',
      itemId: s.itemId,
    });
  });

  // Rule 4 — Dead stock (holding above threshold, no usage in range). Only meaningful
  // when the vendor is actually tracking movement this period.
  if (rows.length > 0) {
    items.forEach((item) => {
      if (item.currentStock <= item.lowStockThreshold) return;
      const s = stats.get(item.id);
      if (s && s.totalUsed > 0) return; // used at least once
      insights.push({
        id: `deadstock-${item.id}`,
        severity: 'INFO',
        title: `${item.name} untouched for ${rangeDays} days`,
        detail: `${formatQuantity(item.currentStock, item.unit)} on hand but no usage in range.`,
        actionLabel: 'Review',
        actionTab: 'items',
        itemId: item.id,
      });
    });
  }

  // CRITICAL → WARN → INFO, stable within a severity (rule order preserved).
  insights.sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity]);
  return insights;
};

const RANGE_PRESETS: { days: number; label: string; testId: string }[] = [
  { days: 7, label: 'Last 7 days', testId: 'insights-range-7d' },
  { days: 14, label: 'Last 14 days', testId: 'insights-range-14d' },
  { days: 30, label: 'Last 30 days', testId: 'insights-range-30d' },
];

const MAX_INSIGHTS = 10;

const InventoryInsightsTab: React.FC<Props> = ({ active, onNavigate }) => {
  const { theme } = useTheme();

  const [days, setDays] = useState(14);
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (rangeDays: number) => {
    const { from, to } = rangeForDays(rangeDays);
    setLoading(true);
    setError(null);
    try {
      const [dashRes, itemsRes] = await Promise.all([
        inventoryApi.getDashboard(from, to),
        inventoryApi.getItems(),
      ]);
      setRows(dashRes.data?.rows || []);
      setItems(itemsRes.data || []);
      setLoaded(true);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, []);

  // Lazy: only fetch once the tab is active, and refetch when the range changes.
  useEffect(() => {
    if (!active) return;
    fetchData(days);
  }, [active, days, fetchData]);

  const { from, to } = useMemo(() => rangeForDays(days), [days]);

  const allInsights = useMemo(
    () => deriveInsights(rows, items, from, to),
    [rows, items, from, to],
  );

  const shown = allInsights.slice(0, MAX_INSIGHTS);
  const moreCount = allInsights.length - shown.length;

  if (!active) return null;

  return (
    <Box>
      {/* Range presets */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        {RANGE_PRESETS.map((p) => {
          const on = days === p.days;
          return (
            <Chip
              key={p.days}
              label={p.label}
              onClick={() => setDays(p.days)}
              data-testid={p.testId}
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 30,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: on ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                color: on ? theme.primary : theme.gray?.[600] || '#666',
                border: on ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                '&:hover': { backgroundColor: on ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
              }}
            />
          );
        })}
        {loading && <CircularProgress size={18} sx={{ color: theme.primary, ml: 1, alignSelf: 'center' }} />}
      </Box>

      {error && (
        <Paper
          elevation={0}
          data-testid="insights-error"
          sx={{ p: 2, mb: 2, border: `1px solid ${theme.danger || '#D32F2F'}`, color: theme.danger || '#D32F2F' }}
        >
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {loading && !loaded ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: theme.primary }} />
        </Box>
      ) : shown.length === 0 ? (
        <Paper
          elevation={0}
          data-testid="insights-empty"
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px solid ${theme.gray?.[200] || '#EEE'}`,
            background: theme.components?.card?.background || '#FFFFFF',
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text }}>
            All calm — nothing needs attention.
          </Typography>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] }}>
            Run-out risk, wastage, trends and dead stock are checked across this range.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {shown.map((ins) => {
            const color = SEV_COLOR(theme, ins.severity);
            return (
              <Paper
                key={ins.id}
                elevation={0}
                data-testid="insight-card"
                data-insight-type={ins.id.split('-')[0]}
                data-severity={ins.severity}
                sx={{
                  p: 1.25,
                  borderLeft: `4px solid ${color}`,
                  border: `1px solid ${theme.gray?.[200] || '#EEE'}`,
                  borderLeftWidth: 4,
                  borderLeftColor: color,
                  background: theme.components?.card?.background || '#FFFFFF',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: color,
                      mt: 0.6,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', lineHeight: 1.25, color: theme.text }}>
                      {ins.title}
                    </Typography>
                    <Typography sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.78rem', mt: 0.4 }}>
                      {ins.detail}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      data-testid="insight-action"
                      onClick={() => onNavigate?.(ins.actionTab, ins.itemId)}
                      sx={{
                        mt: 1,
                        background: color,
                        textTransform: 'none',
                        fontSize: '0.74rem',
                        py: 0.25,
                        px: 1.25,
                        minWidth: 0,
                        boxShadow: 'none',
                        '&:hover': { background: color, opacity: 0.9, boxShadow: 'none' },
                      }}
                    >
                      {ins.actionLabel}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
          {moreCount > 0 && (
            <Typography
              variant="caption"
              data-testid="insights-more"
              sx={{ color: theme.gray?.[500], textAlign: 'center', pt: 0.5 }}
            >
              +{moreCount} more
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default InventoryInsightsTab;
